import {
  Box,
  Step,
  Alert,
  Paper,
  Button,
  Stepper,
  Divider,
  TextField,
  StepLabel,
  Typography,
  IconButton,
  CardContent,
  InputAdornment,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import apiService from 'library/api-service';
import { passwordRules } from 'components/rule-item';
import { FormattedMessage, useIntl } from 'react-intl';
import logoSrc from 'assets/images/idecon-header-logo.png';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FormEvent, useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ColorModeIconDropdown, LanguagePicker, RuleItem } from 'components';

const ForgotPassword = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();

  // useState hooks
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // dynamic variables
  const steps = useMemo(
    () => [
      intl.formatMessage({ id: 'forgotPassword.email', defaultMessage: 'E-Posta' }),
      intl.formatMessage({ id: 'forgotPassword.verifyCode', defaultMessage: 'Kodu Doğrula' }),
      intl.formatMessage({ id: 'forgotPassword.newPassword', defaultMessage: 'Yeni Parola' }),
    ],
    [intl]
  );

  // async helper functions
  const handleSubmitEmail = async (e: FormEvent) => {
    e.preventDefault();

    resetMessages();

    if (!email) {
      setErrorMessage(
        intl.formatMessage({ id: 'forgotPassword.emailRequired', defaultMessage: 'Lütfen e-posta adresinizi girin' })
      );
      return;
    }

    // Idecon-eHesApp email format check
    if (!/^[a-zA-Z0-9._%+-]+@(idecon\.com\.tr|ehesapp\.com)$/.test(email)) {
      setErrorMessage(
        intl.formatMessage({
          id: 'validations.invalidEmailForIdeconAndEhesapp',
          defaultMessage: 'Idecon ya da Ehesapp e-postası girilmelidir',
        })
      );
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.forgotPassword(email);

      if (response.data.success) {
        setActiveStep(1);
        setSuccessMessage(
          intl.formatMessage({
            id: 'forgotPassword.verifyCodeSent',
            defaultMessage: 'Doğrulama kodu e-posta adresinize gönderildi',
          })
        );
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCode = async (e: FormEvent) => {
    e.preventDefault();

    resetMessages();

    if (!resetCode) {
      setErrorMessage(
        intl.formatMessage({
          id: 'forgotPassword.verifyCodeRequired',
          defaultMessage: 'Lütfen gelen doğrulama kodunu girin',
        })
      );
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.verifyResetCode(email, resetCode);

      if (response.data.success) {
        setActiveStep(2);
        setSuccessMessage(
          intl.formatMessage({
            id: 'forgotPassword.verifyCodeChecked',
            defaultMessage: 'Kod doğrulandı. Yeni şifrenizi belirleyebilirsiniz',
          })
        );
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNewPassword = async (e: FormEvent) => {
    e.preventDefault();

    resetMessages();

    if (!newPassword || !confirmPassword) {
      setErrorMessage(
        intl.formatMessage({
          id: 'forgotPassword.passwordRequired',
          defaultMessage: 'Lütfen yeni şifreyi ve tekrarını girin',
        })
      );
      return;
    }

    const regex =
      /^(?!.*(?:[iI][dD][eE][cC][oO][nN]|[eE][hH][eE][sS][aA][pP][pP]))(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!regex.test(newPassword)) {
      setErrorMessage(
        intl.formatMessage({ id: 'validations.invalidPasswordFormat', defaultMessage: 'Geçersiz parola formatı' })
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage(
        intl.formatMessage({
          id: 'forgotPassword.passwordMissmatch',
          defaultMessage: 'Şifre ve şifre tekrarı eşleşmiyor',
        })
      );
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.resetPassword(email, resetCode, newPassword);

      if (response.data.success) {
        setSuccessMessage(
          intl.formatMessage({
            id: 'forgotPassword.passwordReset',
            defaultMessage: 'Şifreniz başarıyla güncellendi. Giriş sayfasına yönlendiriliyorsunuz...',
          })
        );
      }

      setTimeout(() => {
        navigate('/login');
      }, 2500);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const resetMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const onClickLogoHandler = useCallback(() => navigate('/login'), [navigate]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box component="form" onSubmit={handleSubmitEmail} noValidate>
            <TextField
              fullWidth
              label={<FormattedMessage id="forgotPassword.email" defaultMessage="E-Posta" />}
              type="email"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />

            <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 2 }}>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <FormattedMessage id="forgotPassword.sendVerifyCode" defaultMessage="Kod Gönder" />
              )}
            </Button>

            <Box mt={2} textAlign="center">
              <Typography variant="body2">
                <FormattedMessage id="forgotPassword.returnQuestion" defaultMessage="Geri dönmek ister misin?" />{' '}
                <MuiLink component={RouterLink} to="/login">
                  <FormattedMessage id="forgotPassword.login" defaultMessage="Giriş Yap" />
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box component="form" onSubmit={handleSubmitCode} noValidate>
            <TextField
              fullWidth
              label={<FormattedMessage id="forgotPassword.email" defaultMessage="E-Posta" />}
              value={email}
              margin="normal"
              disabled
            />

            <TextField
              fullWidth
              label={<FormattedMessage id="forgotPassword.code" defaultMessage="Doğrulama Kodu" />}
              margin="normal"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
            />

            <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 2 }}>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <FormattedMessage id="forgotPassword.verifyCode" defaultMessage="Kodu Doğrula" />
              )}
            </Button>

            <Box mt={2} textAlign="center">
              <Typography variant="body2">
                <FormattedMessage id="forgotPassword.wrongEmailQuestion" defaultMessage="Yanlış e-posta mı girdin?" />{' '}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => {
                    resetMessages();
                    setActiveStep(0);
                  }}>
                  <FormattedMessage id="forgotPassword.return" defaultMessage="Geri dön" />{' '}
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box component="form" onSubmit={handleSubmitNewPassword} noValidate>
            <TextField
              fullWidth
              label={<FormattedMessage id="forgotPassword.newPassword" defaultMessage="Yeni Parola" />}
              margin="normal"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{ border: 'none' }}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              label={<FormattedMessage id="forgotPassword.newPasswordRetry" defaultMessage="Yeni Parola (Tekrar)" />}
              margin="normal"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{ border: 'none' }}
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                        size="small">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box my={2} display="flex" flexDirection="row" gap={2}>
              <Box display="flex" flexDirection="column" justifyContent="flex-start" gap={0.5}>
                <RuleItem
                  ok={passwordRules.minLength(newPassword)}
                  text={intl.formatMessage({ id: 'passwordRules.minLength', defaultMessage: 'En az 8 karakter' })}
                />
                <RuleItem
                  ok={passwordRules.hasLower(newPassword)}
                  text={intl.formatMessage({ id: 'passwordRules.hasLower', defaultMessage: 'En az 1 küçük harf' })}
                />
                <RuleItem
                  ok={passwordRules.hasSymbol(newPassword)}
                  text={intl.formatMessage({ id: 'passwordRules.hasSymbol', defaultMessage: 'En az 1 özel karakter' })}
                />
              </Box>
              <Box display="flex" flexDirection="column" justifyContent="flex-start" gap={0.5}>
                <RuleItem
                  ok={passwordRules.hasUpper(newPassword)}
                  text={intl.formatMessage({ id: 'passwordRules.hasUpper', defaultMessage: 'En az 1 büyük harf' })}
                />
                <RuleItem
                  ok={passwordRules.hasNumber(newPassword)}
                  text={intl.formatMessage({ id: 'passwordRules.hasNumber', defaultMessage: 'En az 1 rakam' })}
                />
                <RuleItem
                  ok={passwordRules.noForbiddenWords(newPassword)}
                  text={intl.formatMessage({
                    id: 'passwordRules.noForbiddenWords',
                    defaultMessage: 'Idecon / eHesApp içermemeli',
                  })}
                />
              </Box>
            </Box>

            <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 2 }}>
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <FormattedMessage id="forgotPassword.resetPassword" defaultMessage="Şifreyi Yenile" />
              )}
            </Button>

            <Box mt={2} textAlign="center">
              <Typography variant="body2">
                <FormattedMessage id="forgotPassword.rememberQuestion" defaultMessage="Hatırladın mı?" />{' '}
                <MuiLink component={RouterLink} to="/login">
                  <FormattedMessage id="forgotPassword.login" defaultMessage="Giriş Yap" />
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900]),
      }}>
      <Paper elevation={3} sx={{ width: '100%', maxWidth: 420, p: 2 }}>
        <Paper elevation={0}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 20px',
            }}>
            <img style={{ cursor: 'pointer' }} onClick={onClickLogoHandler} width="100px" src={logoSrc} alt="logo" />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}>
              <LanguagePicker />
              <ColorModeIconDropdown />
            </Box>
          </Box>
        </Paper>
        <Divider />
        <br />
        <CardContent>
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            <FormattedMessage id="forgotPassword.forgotPassword" defaultMessage="Şifreni mi Unuttun?" />
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {activeStep === 0 && (
              <FormattedMessage
                id="forgotPassword.stepZeroMessage"
                defaultMessage="E-posta adresini gir, sana doğrulama kodu gönderelim."
              />
            )}
            {activeStep === 1 && (
              <FormattedMessage
                id="forgotPassword.stepOneMessage"
                defaultMessage="E-posta adresine gönderilen doğrulama kodunu gir."
              />
            )}
            {activeStep === 2 && (
              <FormattedMessage
                id="forgotPassword.stepTwoMessage"
                defaultMessage="Kod doğrulandı. Şimdi yeni şifreni belirleyebilirsin."
              />
            )}
          </Typography>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {renderStepContent()}
        </CardContent>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
